import type { InstrumentQueueItemContract, Locale } from "@chams-pay/contracts";
import { operationsMessages, translate } from "@chams-pay/contracts";
import { Badge } from "@chams-pay/design-system/components/badge";
import { Button, buttonVariants } from "@chams-pay/design-system/components/button";
import { Input } from "@chams-pay/design-system/components/input";
import { Label } from "@chams-pay/design-system/components/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@chams-pay/design-system/components/table";
import { LoaderCircle, MessageCircle } from "lucide-react";
import { casablancaDate } from "../../core/format.js";
import { instrumentTransitionCopy } from "../../core/instrument-transition-copy.js";
import type { InstrumentStatusFilter, InstrumentsController } from "../../core/use-operations.js";
import { instrumentBucket } from "../../core/use-operations.js";
import { usePreparedWhatsAppIntent } from "../../core/use-whatsapp-intent.js";
import { DatePicker } from "../../ui/date-picker.js";
import { Amount, Bidi, Count, DateValue } from "../../ui/figures.js";
import {
  CheckCircleGlyph,
  ChequeGlyph,
  ScopedEmpty,
  ScopedError,
  TransferGlyph,
} from "../../ui/states.js";
import { Surface } from "../surfaces.js";

const filterKey = {
  pending: "ops.pending",
  settled: "ops.settled",
  rejected: "ops.rejected",
} as const satisfies Record<InstrumentStatusFilter, Parameters<typeof translate>[1]>;

function SettledInstrumentWhatsAppAction({
  locale,
  csrf,
  paymentId,
}: {
  readonly locale: Locale;
  readonly csrf: string;
  readonly paymentId: string;
}) {
  const text = operationsMessages(locale);
  const whatsapp = usePreparedWhatsAppIntent({ paymentId, locale, csrf, enabled: true });

  if (whatsapp.url) {
    return (
      <a
        href={whatsapp.url}
        target="_blank"
        rel="external noopener noreferrer"
        aria-label={text.whatsapp}
        title={text.whatsapp}
        data-state="ready"
        className={buttonVariants({
          variant: "outline",
          size: "sm",
          className:
            "min-h-11 w-[240px] !border-success bg-white text-xs text-success hover:!border-success hover:!bg-success-soft hover:!text-success",
        })}
        onClick={(event) => event.stopPropagation()}
      >
        <MessageCircle aria-hidden="true" />
        {text.whatsapp}
      </a>
    );
  }

  const actionLabel = whatsapp.failed ? text.whatsappRetry : text.whatsappGenerate;
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      aria-label={whatsapp.preparing ? text.whatsappPreparing : actionLabel}
      title={whatsapp.preparing ? text.whatsappPreparing : actionLabel}
      data-state={whatsapp.preparing ? "preparing" : whatsapp.failed ? "failed" : "idle"}
      className="min-h-11 w-[240px] border-success bg-white text-xs text-success hover:!border-success hover:!bg-success-soft hover:!text-success"
      disabled={whatsapp.preparing}
      onClick={(event) => {
        event.stopPropagation();
        whatsapp.prepare();
      }}
    >
      {whatsapp.preparing ? (
        <LoaderCircle aria-hidden="true" className="animate-spin" />
      ) : (
        <MessageCircle aria-hidden="true" />
      )}
      {whatsapp.preparing
        ? text.whatsappPreparing
        : whatsapp.failed
          ? text.whatsappRetry
          : text.whatsappGenerate}
    </Button>
  );
}

function StatusBadge({
  locale,
  item,
}: {
  readonly locale: Locale;
  readonly item: InstrumentQueueItemContract;
}) {
  const bucket = instrumentBucket(item.status);
  if (bucket === "pending")
    return (
      <Badge
        variant="warning"
        dot
        className="max-w-full whitespace-nowrap px-2 text-[9.5px] leading-none"
      >
        {translate(locale, "ops.awaitingSettlement")}
      </Badge>
    );
  if (bucket === "rejected")
    return (
      <Badge variant="destructive" dot>
        {translate(locale, "ops.rejected")}
      </Badge>
    );
  return (
    <Badge variant="success" dot>
      {translate(locale, "ops.settled")}
    </Badge>
  );
}

export function DesktopInstrumentsScreen({
  locale,
  csrf,
  controller,
  onOpenStudent,
}: {
  readonly locale: Locale;
  readonly csrf: string;
  readonly controller: InstrumentsController;
  readonly onOpenStudent: (studentId: string) => void;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const pending = controller.items.filter((item) => item.status === "pending");
  const pendingTotal = pending.reduce((total, item) => total + BigInt(item.amountMad), 0n);
  const filters: readonly InstrumentStatusFilter[] = ["pending", "settled", "rejected"];
  const transitionCopy = instrumentTransitionCopy(locale, controller.selected, controller.action);
  return (
    <section className="operations-screen flex flex-col gap-5" data-testid="instruments-screen">
      <div className="instrument-queue overflow-hidden rounded-lg border border-border bg-card shadow-e1">
        <header className="operations-heading flex flex-wrap items-center gap-4 border-b border-border/70 p-5">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">
              <Count value={pending.length} /> {t("ops.pendingItems")} ·{" "}
              <Amount value={pendingTotal} locale={locale} />
            </span>
          </div>
          <div
            role="tablist"
            aria-label={t("ops.status")}
            data-tab-count="3"
            data-active-index={String(filters.indexOf(controller.filter))}
            className="sliding-tabs ms-auto box-content flex w-[275px] shrink-0 gap-[3px] overflow-hidden rounded-lg bg-sunken p-[3px]"
          >
            <span className="sliding-tabs__indicator" aria-hidden="true" />
            {filters.map((value) => {
              const active = controller.filter === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => controller.setFilter(value)}
                  className="h-11 min-h-11 rounded-md px-4 font-heading text-xs font-bold"
                >
                  {t(
                    locale === "ar" && value === "pending"
                      ? "ops.pendingShort"
                      : locale === "ar" && value === "settled"
                        ? "ops.settledTab"
                        : filterKey[value],
                  )}
                </button>
              );
            })}
          </div>
        </header>

        {controller.loadFailed ? (
          <div className="p-5">
            <ScopedError locale={locale} onRetry={() => void controller.reload()} />
          </div>
        ) : controller.actionFailed && !controller.selected ? (
          <div className="p-5">
            <ScopedError
              locale={locale}
              title={t("ops.settleFailed")}
              body={t("ops.settleFailedBody")}
              onRetry={() => void controller.reload()}
            />
          </div>
        ) : controller.visible.length ? (
          <>
            <Table className="instrument-list min-w-[1200px] table-fixed">
              <TableHeader>
                <TableRow className="border-border/70 bg-surface-2 hover:bg-surface-2">
                  <TableHead className="chams-eyebrow h-10 w-[20%] text-start">
                    {t("ops.student")}
                  </TableHead>
                  <TableHead className="chams-eyebrow h-10 w-[14%] text-start">
                    {t("ops.reference")}
                  </TableHead>
                  <TableHead className="chams-eyebrow h-10 w-[10%] text-start">
                    {t("ops.method")}
                  </TableHead>
                  <TableHead className="chams-eyebrow h-10 w-[12%] text-start">
                    {t("ops.amount")}
                  </TableHead>
                  <TableHead className="chams-eyebrow h-10 w-[10%] text-start">
                    {t("ops.date")}
                  </TableHead>
                  <TableHead className="chams-eyebrow h-10 w-[11%] text-start">
                    {t("ops.status")}
                  </TableHead>
                  <TableHead className="chams-eyebrow h-10 w-[23%] text-end">
                    {t("ops.actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {controller.visible.map((item) => {
                  const isPending = item.status === "pending";
                  const isSettled = instrumentBucket(item.status) === "settled";
                  return (
                    <TableRow
                      key={item.paymentId}
                      data-status={item.status}
                      tabIndex={0}
                      aria-label={`${locale === "ar" ? item.student.nameAr : item.student.nameFr} · ${item.reference}`}
                      className="instrument-row chams-focusable cursor-pointer border-border/60 hover:bg-sunken/80"
                      onClick={() => onOpenStudent(item.student.id)}
                      onKeyDown={(event) => {
                        if (
                          event.target === event.currentTarget &&
                          (event.key === "Enter" || event.key === " ")
                        ) {
                          event.preventDefault();
                          onOpenStudent(item.student.id);
                        }
                      }}
                    >
                      <TableCell className="py-3.5 text-start">
                        <span className="flex flex-col gap-0.5">
                          <strong className="font-heading text-[13px] leading-tight">
                            {locale === "ar" ? item.student.nameAr : item.student.nameFr}
                          </strong>
                          <span className="text-[11px] text-muted-2">
                            {locale === "ar" ? item.student.nameFr : item.student.nameAr}
                          </span>
                        </span>
                      </TableCell>
                      <TableCell className="py-3.5 text-start">
                        <Bidi className="text-xs font-semibold text-foreground/80">
                          {item.reference}
                        </Bidi>
                      </TableCell>
                      <TableCell className="py-3.5 text-start">
                        <span className="flex items-center gap-1.5 font-heading text-[11.5px] font-semibold text-foreground/80">
                          {item.method.method === "cheque" ? <ChequeGlyph /> : <TransferGlyph />}
                          {t(item.method.method === "cheque" ? "ops.cheque" : "ops.transfer")}
                        </span>
                      </TableCell>
                      <TableCell className="py-3.5 text-start">
                        <Amount
                          value={item.amountMad}
                          locale={locale}
                          className="text-sm font-extrabold"
                        />
                      </TableCell>
                      <TableCell className="py-3.5 text-start">
                        <DateValue
                          value={item.receivedDate}
                          className="text-[11.5px] font-semibold text-muted-foreground"
                        />
                      </TableCell>
                      <TableCell className="py-3.5 text-start">
                        <StatusBadge locale={locale} item={item} />
                      </TableCell>
                      <TableCell className="py-3.5 text-end">
                        {isPending && (
                          <span className="inline-flex flex-nowrap items-center justify-end gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="default"
                              className="min-h-11 whitespace-nowrap"
                              onClick={(event) => {
                                event.stopPropagation();
                                controller.begin(item, "settle");
                              }}
                            >
                              {t("ops.confirm")}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="destructive"
                              className="min-h-11 whitespace-nowrap"
                              onClick={(event) => {
                                event.stopPropagation();
                                controller.begin(item, "reject");
                              }}
                            >
                              {t("ops.reject")}
                            </Button>
                          </span>
                        )}
                        {isSettled && (
                          <SettledInstrumentWhatsAppAction
                            locale={locale}
                            csrf={csrf}
                            paymentId={item.paymentId}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div className="flex flex-wrap items-center gap-3 border-t border-border/70 bg-surface-2 px-5 py-3.5">
              <strong className="ms-auto font-heading text-xs font-bold">
                {t("ops.queueTotal")} <Amount value={controller.visibleTotalMad} locale={locale} />
              </strong>
            </div>
          </>
        ) : (
          <div className="p-5">
            <ScopedEmpty
              tone="success"
              glyph={<CheckCircleGlyph />}
              title={controller.filter === "settled" ? t("ops.settledEmpty") : t("ops.queueEmpty")}
              body={
                controller.filter === "settled"
                  ? t("ops.settledEmptyBody")
                  : t("ops.queueEmptyBody")
              }
            />
          </div>
        )}
      </div>

      <Surface
        open={controller.selected !== null}
        onOpenChange={(open) => {
          if (!open) controller.dismiss();
        }}
        title={transitionCopy.title}
        description={transitionCopy.subtitle}
        closeLabel={t("common.close")}
        testId="instrument-transition-surface"
        footer={
          <div className="operations-surface-actions flex flex-1 flex-wrap items-center gap-2">
            <Button variant="outline" className="min-h-11" onClick={controller.dismiss}>
              {t("ops.cancel")}
            </Button>
            <Button
              form="instrument-transition-form"
              type="submit"
              className="ms-auto min-h-11"
              variant={controller.action === "reject" ? "destructive-solid" : "success"}
              disabled={controller.submitDisabled}
            >
              {transitionCopy.title}
            </Button>
          </div>
        }
      >
        <form
          id="instrument-transition-form"
          className="operations-form flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void controller.submit();
          }}
        >
          <Label className="flex flex-col items-stretch gap-1.5 text-start">
            <span className="font-heading text-[11.5px] font-bold text-foreground/75">
              {transitionCopy.dateLabel} <span className="text-destructive">*</span>
            </span>
            <DatePicker
              locale={locale}
              label={transitionCopy.dateLabel}
              max={casablancaDate()}
              value={controller.statementDate}
              onChange={controller.setStatementDate}
            />
          </Label>

          {controller.action === "settle" ? (
            <>
              <Label className="flex flex-col items-stretch gap-1.5 text-start">
                <span className="font-heading text-[11.5px] font-bold text-foreground/75">
                  {transitionCopy.fieldLabel}{" "}
                  <span className="font-normal text-muted-2">{t("ops.optional")}</span>
                </span>
                <Input
                  className="chams-figure h-11"
                  dir="ltr"
                  value={controller.statementReference}
                  maxLength={120}
                  onChange={(event) => controller.setStatementReference(event.target.value)}
                />
              </Label>
              <p className="rounded-lg border border-success-border bg-success-soft p-3.5 text-[11.5px] leading-relaxed text-success">
                {transitionCopy.note}
              </p>
            </>
          ) : (
            <Label className="flex flex-col items-stretch gap-1.5 text-start">
              <span className="font-heading text-[11.5px] font-bold text-foreground/75">
                {transitionCopy.fieldLabel} <span className="text-destructive">*</span>
              </span>
              <textarea
                className="min-h-24 rounded-md border border-input bg-card px-3 py-2 text-sm"
                value={controller.reason}
                minLength={3}
                maxLength={500}
                required
                onChange={(event) => controller.setReason(event.target.value)}
              />
            </Label>
          )}

          {controller.actionFailed && (
            <ScopedError
              locale={locale}
              title={transitionCopy.failureTitle}
              body={t("ops.settleFailedBody")}
              onRetry={() => void controller.submit()}
            />
          )}
        </form>
      </Surface>
    </section>
  );
}
